import {
  // oxlint-disable-next-line no-restricted-imports
  lazy,
  Suspense,
  useCallback,
} from 'react';
import type {
  ComponentProps,
  ComponentType,
  LazyExoticComponent,
  ReactNode,
} from 'react';

import { useFirstRenderType } from '#/hooks/useFirstRenderType';
import { useIsomorphicLayoutEffect } from '#/hooks/useIsomorphicLayoutEffect';
import { useLatest } from '#/hooks/useLatest';
import { ViewportBreakpoint } from '#/theme/theme';

import { log } from './logging';
import { useRscFriendlyState } from './useRscFriendlyState';
import { getViewportBreakpoint } from './viewport/getViewportBreakpoint';

const MAX_TRY_COUNT = 5;
const RETRY_INTERVAL_MS = 1_000;

// oxlint-disable-next-line typescript/no-explicit-any
function lazyWithRetry<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  const retryImport = async () => {
    for (let i = 0; i < MAX_TRY_COUNT; i++) {
      try {
        // oxlint-disable-next-line no-await-in-loop
        return await loader();
      } catch (error) {
        if (i === MAX_TRY_COUNT - 1) {
          log.info('Unable to load lazy component after retries');
          throw error;
        }

        // oxlint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL_MS));
      }
    }

    // This should never happen, this code is here so we get correct types
    throw new Error('Lazy with retry failed loop');
  };

  return lazy(retryImport);
}

type RenderOptions =
  | { strategy: 'always'; placeholder?: ReactNode }
  | { strategy: 'client-only'; placeholder?: ReactNode }
  | { strategy: 'client-desktop-only'; placeholder?: ReactNode };

// oxlint-disable-next-line typescript/no-explicit-any
export function dynamic<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  render: RenderOptions,
): ComponentType<ComponentProps<T>> {
  const LazyComponent = lazyWithRetry(loader);

  function DynamicComponent(props: ComponentProps<T>) {
    const { firstRenderType } = useFirstRenderType();
    const [display, setDisplay] = useRscFriendlyState<
      'placeholder' | 'component' | false
    >(() => {
      if (render.strategy === 'always') return 'component';
      return render.placeholder ? 'placeholder' : false;
    });

    const displayRef = useLatest(display);

    const updateDisplay = useCallback(() => {
      const viewportBreakpoint = getViewportBreakpoint();
      const isMobile =
        viewportBreakpoint === ViewportBreakpoint.xs ||
        viewportBreakpoint === ViewportBreakpoint.sm;

      const newDisplay =
        render.strategy === 'client-desktop-only' && isMobile
          ? false
          : 'component';

      if (newDisplay !== displayRef.current) setDisplay(newDisplay);
    }, [displayRef, setDisplay]);

    if (typeof window !== 'undefined') {
      // oxlint-disable-next-line react-hooks/rules-of-hooks
      useIsomorphicLayoutEffect(() => {
        updateDisplay();
      }, [updateDisplay]);
    }

    if (!display) return null;

    if (display === 'placeholder' && render.placeholder) {
      // oxlint-disable-next-line react/jsx-no-useless-fragment
      return <>{render.placeholder}</>;
    }

    const component = (
      <LazyComponent
        // oxlint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    );

    return render.strategy === 'always' && firstRenderType === 'server' ? (
      // Don't use suspense for server-side-rendered components because
      // it causes a flicker
      component
    ) : (
      <Suspense fallback={render.placeholder}>{component}</Suspense>
    );
  }

  const lazyDisplayName =
    'displayName' in LazyComponent &&
    typeof LazyComponent.displayName === 'string'
      ? LazyComponent.displayName
      : null;

  DynamicComponent.displayName = `Dynamic(${
    lazyDisplayName ?? LazyComponent.name ?? 'Component'
  })`;

  return DynamicComponent;
}
